/**
 * API Service for Admin Portal
 * Communicates with the backend for all administrative operations
 */

// Get configuration from runtime config if available, otherwise use environment variables
const runtimeConfig = window.RUNTIME_CONFIG || {};

// Default base URL for the API
const API_BASE_URL = runtimeConfig.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8787';
const BUSINESS_API_URL = runtimeConfig.REACT_APP_BUSINESS_API_URL || process.env.REACT_APP_BUSINESS_API_URL || 'http://localhost:8789';

console.log('API Configuration:', { API_BASE_URL, BUSINESS_API_URL });

// Direct worker URLs for local development
const WORKER_URLS = {
  business: BUSINESS_API_URL,        // Business worker runs on its own port
  auth: API_BASE_URL,                // Auth worker
  default: API_BASE_URL              // Default worker
};

// Helper function to get the appropriate base URL for different endpoints
function getBaseUrlForEndpoint(endpoint) {
  if (endpoint.startsWith('/admin/businesses')) {
    console.log('Using business worker URL for', endpoint);
    return WORKER_URLS.business;
  }
  if (endpoint.startsWith('/auth/admin')) {
    console.log('Using auth worker URL for', endpoint);
    return WORKER_URLS.auth;
  }
  return WORKER_URLS.default;
}

/**
 * Custom fetch with authentication and error handling
 */
async function fetchWithAuth(endpoint, options = {}) {
  // Get authentication token from storage (specific for admin)
  const token = localStorage.getItem('adminToken');

  // Default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add authentication if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Check if admin is operating in a specific business context
  const selectedBusinessId = localStorage.getItem('selectedBusinessId');
  if (selectedBusinessId) {
    headers['X-Business-Id'] = selectedBusinessId;
  }

  // Prepare request options
  const requestOptions = {
    ...options,
    headers
  };

  try {
    // Get the appropriate base URL for this endpoint
    const baseUrl = getBaseUrlForEndpoint(endpoint);
    
    // Log request details for debugging
    console.log(`Sending request to: ${baseUrl}${endpoint}`, {
      method: requestOptions.method || 'GET',
      headers: requestOptions.headers,
      body: requestOptions.body ? '(request has body)' : undefined
    });
    
    // Make the fetch request
    const response = await fetch(`${baseUrl}${endpoint}`, requestOptions);

    // Log response status for debugging
    console.log(`Response from ${endpoint}:`, {
      status: response.status,
      statusText: response.statusText
    });

    // If response is 401 Unauthorized, try to refresh the token
    if (response.status === 401) {
      console.log('Received 401 Unauthorized, attempting to refresh token');
      
      // Before attempting refresh, check if this is a retry after refresh
      const isRetryAfterRefresh = options.isRetryAfterRefresh;
      
      // If this is already a retry, we don't want to enter an infinite loop
      if (isRetryAfterRefresh) {
        console.log('This is already a retry after token refresh - not retrying again');
        
        // Clone the response to read its content for better debugging
        try {
          const clonedResponse = response.clone();
          const responseText = await clonedResponse.text();
          console.log('401 response content on retry:', responseText);
        } catch (e) {
          console.log('Could not read 401 response content');
        }
        
        throw new Error('Authentication failed even after token refresh. Please log in again.');
      }
      
      try {
        const refreshed = await refreshAdminToken();
        if (refreshed) {
          console.log('Token refresh successful, retrying original request');
          // Get the new token from localStorage (should have been updated by refreshAdminToken)
          const newToken = localStorage.getItem('adminToken');
          console.log('New token from localStorage:', newToken ? 'present' : 'missing');
          
          if (!newToken) {
            console.error('Token refresh succeeded but no new token in localStorage');
            throw new Error('Authentication error after token refresh.');
          }
          
          // Retry with new token - create a new options object with the updated token
          const updatedOptions = { ...options };
          if (!updatedOptions.headers) {
            updatedOptions.headers = {};
          }
          updatedOptions.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Mark this request as a retry to prevent infinite loops
          updatedOptions.isRetryAfterRefresh = true;
          
          console.log('Retrying request with new token');
          return fetchWithAuth(endpoint, updatedOptions);
        } else {
          console.log('Token refresh returned false');
          
          // Log more details about the failed request
          console.log('Failed request details:', {
            endpoint,
            method: options.method || 'GET'
          });
          
          throw new Error('Session expired. Please log in again.');
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        throw new Error('Session expired. Unable to refresh authentication.');
      }
    }

    // Handle 403 Forbidden
    if (response.status === 403) {
      console.error('403 Forbidden response');
      throw new Error('You do not have permission to access this resource.');
    }

    // Handle 404 Not Found
    if (response.status === 404) {
      console.error('404 Not Found response');
      
      // Clone the response and get the body content for debugging
      const clonedResponse = response.clone();
      const responseText = await clonedResponse.text();
      console.log('Response body:', responseText);
      
      // Special handling for business endpoints that return 404
      if (endpoint.includes('/businesses/')) {
        throw new Error('Business not found. Please select a valid business.');
      } else {
        throw new Error('The requested resource was not found.');
      }
    }

    // Handle 500 Server Error
    if (response.status >= 500) {
      console.error('500 Server Error response');
      throw new Error('An error occurred on the server. Please try again later.');
    }

    // Handle other non-2xx responses
    if (!response.ok) {
      console.error(`Error response: ${response.status} ${response.statusText}`);
      
      // Clone the response and get the body content for debugging
      const clonedResponse = response.clone();
      const responseText = await clonedResponse.text();
      console.log('Error response body:', responseText);
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'An unexpected error occurred');
    }

    // Parse the JSON response
    const jsonResponse = await response.json();
    
    // Check if the response has the standard API format with success and data properties
    if (jsonResponse && jsonResponse.success === true && jsonResponse.data !== undefined) {
      console.log('Response contains success wrapper, returning data:', jsonResponse.data);
      return jsonResponse.data;
    }
    
    // Otherwise return the raw JSON response
    return jsonResponse;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Refresh admin authentication token
 */
async function refreshAdminToken() {
  console.log('refreshAdminToken: Starting token refresh process');
  const refreshToken = localStorage.getItem('adminRefreshToken');
  const existingToken = localStorage.getItem('adminToken');
  
  if (!refreshToken) {
    console.log('No refresh token found in localStorage');
    return false;
  }

  try {
    console.log('Preparing refresh token request');
    const baseUrl = getBaseUrlForEndpoint('/auth/admin/refresh');
    console.log('Using base URL for refresh:', baseUrl);
    
    // First, try to use the /auth/admin/verify endpoint to check if the current token is still valid
    // This can prevent unnecessary token refreshes
    try {
      console.log('Trying to verify current token first');
      const verifyResponse = await fetch(`${baseUrl}/auth/admin/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${existingToken}`
        }
      });
      
      if (verifyResponse.ok) {
        console.log('Current token is still valid, no need to refresh');
        return true; // Token is still valid, no need to refresh
      }
      console.log('Token verification failed, proceeding with refresh');
    } catch (verifyError) {
      console.log('Token verification check failed, proceeding with refresh', verifyError);
      // Continue with token refresh
    }
    
    // Proceed with token refresh
    const response = await fetch(`${baseUrl}/auth/admin/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });

    console.log('Refresh token response status:', response.status);
    
    if (!response.ok) {
      console.log('Refresh token request failed with status:', response.status);
      // Try to log the response body for debugging
      try {
        const errorText = await response.text();
        console.log('Refresh token error response:', errorText);
      } catch (e) {
        console.log('Could not read error response body');
      }
      
      // If refresh fails, but we still have an existing token, let's keep using it
      // This helps with the temporary failure scenario
      if (existingToken) {
        console.log('Keeping existing token despite refresh failure');
        return true;
      }
      
      return false;
    }

    let responseObj;
    try {
      responseObj = await response.json();
      console.log('Refresh token response parsed successfully');
    } catch (e) {
      console.error('Failed to parse refresh token response as JSON:', e);
      // If parsing fails but we have an existing token, continue using it
      if (existingToken) {
        console.log('Keeping existing token despite JSON parse failure');
        return true;
      }
      return false;
    }
    
    // Extract the data we need for authentication
    // The API returns { success: true, data: { accessToken, refreshToken } }
    // But handle different response formats gracefully
    
    let accessToken, newRefreshToken;
    
    if (responseObj.success && responseObj.data) {
      // Standard format
      accessToken = responseObj.data.accessToken;
      newRefreshToken = responseObj.data.refreshToken;
    } else if (responseObj.accessToken) {
      // Direct format without success wrapper
      accessToken = responseObj.accessToken;
      newRefreshToken = responseObj.refreshToken || refreshToken; // Use old refresh token if none provided
    } else {
      console.error('Unexpected refresh response format:', responseObj);
      
      // If format is unexpected but we have an existing token, continue using it
      if (existingToken) {
        console.log('Keeping existing token despite unexpected response format');
        return true;
      }
      
      return false;
    }
    
    if (!accessToken) {
      console.error('Missing access token in refresh response');
      
      // If no new token but we have an existing token, continue using it
      if (existingToken) {
        console.log('Keeping existing token despite missing new token');
        return true;
      }
      
      return false;
    }
    
    // Always store the new access token
    localStorage.setItem('adminToken', accessToken);
    
    // Only update refresh token if we received a new one
    if (newRefreshToken) {
      localStorage.setItem('adminRefreshToken', newRefreshToken);
    }
    
    console.log('Updated tokens after refresh, new tokens in localStorage:', {
      adminToken: !!localStorage.getItem('adminToken'),
      adminRefreshToken: !!localStorage.getItem('adminRefreshToken')
    });
    
    return true;
  } catch (error) {
    console.error('Token refresh failed with exception:', error);
    
    // If there's an exception but we have an existing token, continue using it
    if (existingToken) {
      console.log('Keeping existing token despite refresh exception');
      return true;
    }
    
    return false;
  }
}

/**
 * Admin API service methods
 */
const AdminApiService = {
  // Authentication
  auth: {
    login: async (email, password) => {
      // Use the direct admin login endpoint
      const baseUrl = getBaseUrlForEndpoint('/login');
      const response = await fetch(`${baseUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      }).then(response => {
        if (!response.ok) {
          throw new Error('Login failed');
        }
        return response.json();
      });
      
      console.log('Login response:', response);
      
      // Handle the direct response format from the auth worker
      // The auth worker returns: { success: true, data: { accessToken, refreshToken, user } } 
      //    OR just { accessToken, refreshToken, user }
      const data = response.data || response;
      
      // Extract tokens and user data
      const accessToken = data.accessToken;
      const refreshToken = data.refreshToken || data.accessToken; // Use accessToken as fallback
      const user = data.user;
      
      console.log('Extracted data:', { accessToken, refreshToken, user });
      
      if (!accessToken) {
        console.error('Missing tokens in response:', data);
        throw new Error('Authentication tokens missing from response');
      }
      
      // Store tokens in localStorage for authentication
      localStorage.setItem('adminToken', accessToken);
      localStorage.setItem('adminRefreshToken', refreshToken || accessToken);
      
      // Log the stored values for debugging
      console.log('Stored tokens:', {
        adminToken: localStorage.getItem('adminToken'),
        adminRefreshToken: localStorage.getItem('adminRefreshToken')
      });
      
      return user;
    },
    
    logout: () => {
      // Clear authentication tokens and business context
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('selectedBusinessId');
    },
    
    verifyToken: () => {
      return fetchWithAuth('/auth/admin/verify');
    },
    
    getUserData: () => {
      return fetchWithAuth('/auth/admin/user');
    },
    
    refreshToken: async () => {
      return refreshAdminToken();
    },
    
    // User management (admin only)
    createUser: (userData) => {
      return fetchWithAuth('/auth/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    },
    
    updateUser: (userId, userData) => {
      return fetchWithAuth(`/auth/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData)
      });
    },
    
    deleteUser: (userId) => {
      return fetchWithAuth(`/auth/admin/users/${userId}`, {
        method: 'DELETE'
      });
    },
    
    listUsers: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return fetchWithAuth(`/auth/admin/users?${queryParams}`);
    }
  },
  
  // Business management
  businesses: {
    // Get all businesses (super admin only)
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return fetchWithAuth(`/admin/businesses?${queryParams}`);
    },
    
    // Get business by ID
    getById: async (id) => {
      try {
        return await fetchWithAuth(`/admin/businesses/${id}`);
      } catch (error) {
        console.error(`Error fetching business ${id}:`, error);
        // Re-throw the error to allow the component to handle it properly
        throw error;
      }
    },
    
    // Create new business
    create: (businessData) => {
      return fetchWithAuth('/admin/businesses', {
        method: 'POST',
        body: JSON.stringify(businessData)
      });
    },
    
    // Update business
    update: (id, businessData) => {
      // Check if we're just updating the subdomain
      if (businessData.subdomain && Object.keys(businessData).length <= 2) {
        console.log('Using specialized subdomain update endpoint');
        // Use the specialized endpoint for subdomain updates
        return fetchWithAuth(`/admin/businesses/${id}/subdomain`, {
          method: 'PUT',
          body: JSON.stringify({ subdomain: businessData.subdomain })
        });
      }
      
      // For all other updates, use the standard endpoint
      return fetchWithAuth(`/admin/businesses/${id}`, {
        method: 'PUT',
        body: JSON.stringify(businessData)
      });
    },
    
    // Delete business
    delete: (id) => {
      return fetchWithAuth(`/admin/businesses/${id}`, {
        method: 'DELETE'
      });
    },
    
    // Get business statistics
    getStats: (id) => fetchWithAuth(`/admin/businesses/${id}/stats`),
    
    // Set active business context
    setActive: (id) => {
      localStorage.setItem('selectedBusinessId', id);
    },
    
    // Clear active business context
    clearActive: () => {
      localStorage.removeItem('selectedBusinessId');
    },

    // Update business status (activate or deactivate)
    updateStatus: (id, status) => {
      return fetchWithAuth(`/admin/businesses/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
    }
  },
  
  // Menu management
  menu: {
    // Get all menu items for a business
    getAll: (businessId) => fetchWithAuth(`/admin/businesses/${businessId}/menu`),
    
    // Get menu item by ID
    getById: (businessId, itemId) => fetchWithAuth(`/admin/businesses/${businessId}/menu/${itemId}`),
    
    // Create menu item
    create: (businessId, menuItem) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/items`, {
        method: 'POST',
        body: JSON.stringify(menuItem)
      });
    },
    
    // Update menu item
    update: (businessId, itemId, menuItem) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(menuItem)
      });
    },
    
    // Delete menu item
    delete: (businessId, itemId) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/items/${itemId}`, {
        method: 'DELETE'
      });
    },
    
    // Get menu categories
    getCategories: (businessId) => fetchWithAuth(`/admin/businesses/${businessId}/menu/categories`),
    
    // Create category
    createCategory: (businessId, category) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/categories`, {
        method: 'POST',
        body: JSON.stringify(category)
      });
    },
    
    // Update category
    updateCategory: (businessId, categoryId, category) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/categories/${categoryId}`, {
        method: 'PUT',
        body: JSON.stringify(category)
      });
    },
    
    // Delete category
    deleteCategory: (businessId, categoryId) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/categories/${categoryId}`, {
        method: 'DELETE'
      });
    },
    
    // Reorder menu items
    reorder: (businessId, orderedItems) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/menu/reorder`, {
        method: 'POST',
        body: JSON.stringify({ items: orderedItems })
      });
    }
  },
  
  // Order management
  orders: {
    // Get all orders for a business
    getAll: (businessId, params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return fetchWithAuth(`/admin/businesses/${businessId}/orders?${queryParams}`);
    },
    
    // Get order by ID
    getById: (businessId, orderId) => fetchWithAuth(`/admin/businesses/${businessId}/orders/${orderId}`),
    
    // Update order status
    updateStatus: (businessId, orderId, status) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
    },
    
    // Cancel order
    cancel: (businessId, orderId, reason) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/orders/${orderId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
    },
    
    // Get order statistics
    getStats: (businessId, timeframe = 'week') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/orders/stats?timeframe=${timeframe}`);
    }
  },
  
  // Integration management
  integrations: {
    // Get all integrations for a business
    getAll: (businessId) => fetchWithAuth(`/admin/businesses/${businessId}/integrations`),
    
    // Get integration by type
    getByType: (businessId, type) => fetchWithAuth(`/admin/businesses/${businessId}/integrations/${type}`),
    
    // Update integration settings
    update: (businessId, type, settings) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/integrations/${type}`, {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
    },
    
    // Test integration connection
    test: (businessId, type) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/integrations/${type}/test`, {
        method: 'POST'
      });
    },
    
    // Twilio specific endpoints
    twilio: {
      getPhoneNumbers: (businessId) => fetchWithAuth(`/admin/businesses/${businessId}/integrations/twilio/phone-numbers`),
      
      purchasePhoneNumber: (businessId, areaCode) => {
        return fetchWithAuth(`/admin/businesses/${businessId}/integrations/twilio/phone-numbers`, {
          method: 'POST',
          body: JSON.stringify({ areaCode })
        });
      },
      
      releasePhoneNumber: (businessId, phoneNumberSid) => {
        return fetchWithAuth(`/admin/businesses/${businessId}/integrations/twilio/phone-numbers/${phoneNumberSid}`, {
          method: 'DELETE'
        });
      }
    },
    
    // Toast POS specific endpoints
    toast: {
      sync: (businessId) => {
        return fetchWithAuth(`/admin/businesses/${businessId}/integrations/toast/sync`, {
          method: 'POST'
        });
      },
      
      getStatus: (businessId) => fetchWithAuth(`/admin/businesses/${businessId}/integrations/toast/status`)
    }
  },
  
  // Analytics
  analytics: {
    // Get dashboard overview statistics
    getDashboardStats: async (businessId) => {
      try {
        return await fetchWithAuth(`/admin/businesses/${businessId}/analytics/dashboard`);
      } catch (error) {
        console.error(`Error fetching dashboard stats for business ${businessId}:`, error);
        // Return empty stats instead of throwing error to prevent UI issues
        return {
          recentOrders: { count: 0, trend: "0%", data: [] },
          revenue: { total: 0, trend: "0%", data: [] },
          customers: { count: 0, trend: "0%", newCustomers: 0 },
          popularItems: []
        };
      }
    },
    
    // Get voice analytics
    getVoiceAnalytics: (businessId, timeframe = 'month') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/voice?timeframe=${timeframe}`);
    },
    
    // Get chat analytics
    getChatAnalytics: (businessId, timeframe = 'month') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/chat?timeframe=${timeframe}`);
    },
    
    // Get revenue analytics
    getRevenueAnalytics: (businessId, timeframe = 'month') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/revenue?timeframe=${timeframe}`);
    },
    
    // Get order analytics
    getOrderAnalytics: (businessId, timeframe = 'month') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/orders?timeframe=${timeframe}`);
    },
    
    // Get AI usage metrics
    getAIUsageMetrics: (businessId, timeframe = 'month') => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/ai-usage?timeframe=${timeframe}`);
    },
    
    // Export analytics data
    exportData: (businessId, options) => {
      return fetchWithAuth(`/admin/businesses/${businessId}/analytics/export`, {
        method: 'POST',
        body: JSON.stringify(options)
      });
    }
  }
};

export default AdminApiService;